/**
 * OnThisPageNav — the documentation-style "On this page" rail for a long
 * single-scroll page. A sticky, right-aligned table of contents whose visual
 * language mirrors the LEFT navigation so the two sides read as one system:
 *
 *   • optional GROUP labels (clusters of related sections) with indented item
 *     links beneath a faint guide line — the settings-nav grammar.
 *   • a single foreground active marker that SLIDES to the section in view
 *     (scroll-spy via {@link useActiveHeading}) — the ModeRail animation.
 *   • optional per-item `hint` rendered right-aligned (e.g. an at-a-glance
 *     "Log only" / count), so the rail can double as a config summary.
 *
 * Two ways to pass content:
 *   <OnThisPageNav sections={[{id,label}, …]} />              // flat
 *   <OnThisPageNav groups={[{label, items:[{id,label,hint}]}]} />  // grouped
 *
 * Pure presentation: the page owns the section list and renders a matching
 * `<section id>` per item. Sticky positioning + responsive hiding are the
 * caller's job (pass `className`), so the same rail works docked on the right of
 * a wide page or stacked above content on a narrow one.
 */

import * as React from "react";
import { cn } from "../utils";
import { useActiveHeading } from "./use-scroll-spy";
import { useActiveMarker, NavRailMarker } from "./nav-rail-marker";

export interface OnThisPageSection {
  /** The DOM id of the matching `<section>` — the scroll target + spy anchor. */
  id: string;
  label: string;
  /** Optional at-a-glance state, rendered right-aligned (e.g. "Log only"). */
  hint?: React.ReactNode;
}

export type OnThisPageItem = OnThisPageSection;

export interface OnThisPageGroup {
  /** Group heading. Omit for an ungrouped (flat) cluster. */
  label?: string;
  items: OnThisPageItem[];
}

export interface OnThisPageNavProps {
  /** Flat list of sections (back-compat). Ignored when `groups` is passed. */
  sections?: OnThisPageSection[];
  /** Grouped sections — named clusters of items. Wins over `sections`. */
  groups?: OnThisPageGroup[];
  /** Rail heading. Default "On this page". */
  title?: string;
  className?: string;
}

function scrollToAnchor(
  e: React.MouseEvent<HTMLAnchorElement>,
  id: string,
) {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  history.replaceState(null, "", `#${id}`);
}

export function OnThisPageNav({
  sections,
  groups,
  title = "On this page",
  className,
}: OnThisPageNavProps) {
  const resolvedGroups = React.useMemo<OnThisPageGroup[]>(
    () => groups ?? [{ items: sections ?? [] }],
    [groups, sections],
  );
  const ids = React.useMemo(
    () => resolvedGroups.flatMap((g) => g.items.map((i) => i.id)),
    [resolvedGroups],
  );
  const active = useActiveHeading(ids);
  // The sliding active marker (shared with the left settings nav + ModeRail).
  const { ref: rootRef, top: markerTop } = useActiveMarker(active);

  if (ids.length === 0) return null;

  return (
    <nav aria-label={title} className={cn("type-body", className)}>
      <p className="mb-2 pl-3 type-subtext font-medium uppercase tracking-wide text-muted-foreground/70">
        {title}
      </p>
      <div ref={rootRef} className="relative flex flex-col gap-3">
        <NavRailMarker top={markerTop} />
        {resolvedGroups.map((group, gi) => (
          <div key={group.label ?? `g${gi}`}>
            {group.label ? (
              <p className="mb-1 pl-3 type-body font-medium tracking-tight text-foreground">
                {group.label}
              </p>
            ) : null}
            {/* The faint guide line is the items' border-l (like the settings
                rail); the foreground marker above slides along it. */}
            <div className="flex flex-col border-l border-border/40">
              {group.items.map((item) => {
                const isActive = item.id === active;
                return (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => scrollToAnchor(e, item.id)}
                    data-active={isActive ? "true" : undefined}
                    aria-current={isActive ? "true" : undefined}
                    className={cn(
                      "flex items-center justify-between gap-2 py-1 pl-3 pr-2 tracking-tight transition-colors",
                      isActive
                        ? "font-medium text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <span className="truncate">{item.label}</span>
                    {item.hint != null ? (
                      <span className="shrink-0 type-subtext text-muted-foreground/70">
                        {item.hint}
                      </span>
                    ) : null}
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
}
